import './styles.css';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { SessionProvider } from './features/auth/session';
// Registers the API client with @chipn/queries before any screen renders.
import './lib/api';
import { env } from './lib/env';
import { queryClient } from './lib/query-client';
import { supabase } from './lib/supabase';
import { watchSystemTheme } from './lib/theme';
import { createAppRouter } from './router';

// Sentry is loaded only when configured, keeping it out of the main bundle.
if (env.sentryDsn) {
  const dsn = env.sentryDsn;
  void import('@sentry/react').then((Sentry) =>
    Sentry.init({ dsn, sendDefaultPii: false, tracesSampleRate: 0 }),
  );
}
watchSystemTheme();

async function start(): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const router = createAppRouter({ queryClient, session: data.session });

  function App() {
    const onSessionChange = useCallback((session: typeof data.session) => {
      router.update({ ...router.options, context: { queryClient, session } });
      void router.invalidate();
    }, []);
    return (
      <SessionProvider initial={data.session} onChange={onSessionChange}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </SessionProvider>
    );
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void start();
